import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Package,
  RefreshCw,
  RotateCcw,
  Search,
  User,
  Hash,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { EmptyState } from '../inhouse/ui/empty-state'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { getAdminId } from '../../utils/getAdminId'
import type { OrderReturn, OrderReturnItem } from '../../types/returns'

interface ReturnWithItems extends OrderReturn {
  items: OrderReturnItem[]
}

interface OutboxStatus {
  id: string
  status: string
  last_error: string | null
  attempts: number
}

function ReturnThumb({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false)
  if (failed || !src) {
    return (
      <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
        <Package className="h-4 w-4 text-slate-400" />
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      crossOrigin="anonymous"
      onError={() => setFailed(true)}
      className="w-10 h-10 rounded object-contain bg-slate-100 dark:bg-slate-800 shrink-0"
    />
  )
}

function syncLabel(statuses: OutboxStatus[]): { label: string; className: string } {
  if (statuses.length === 0) {
    return {
      label: 'Local only',
      className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    }
  }
  if (statuses.every((s) => s.status === 'synced' || s.status === 'resolved')) {
    return {
      label: 'Shopify synced',
      className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    }
  }
  if (statuses.some((s) => s.status === 'failed')) {
    return {
      label: 'Shopify failed',
      className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    }
  }
  return {
    label: 'Shopify pending',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  }
}

export function ReturnsReport() {
  const { user } = useAuth()
  const adminId = useMemo(() => getAdminId(user), [user])

  const [loading, setLoading] = useState(true)
  const [returns, setReturns] = useState<ReturnWithItems[]>([])
  const [outboxById, setOutboxById] = useState<Record<string, OutboxStatus>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!adminId) return
    setLoading(true)
    try {
      let query = supabase
        .from('order_returns')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false })
        .limit(500)

      if (dateRange.start) query = query.gte('created_at', `${dateRange.start}T00:00:00`)
      if (dateRange.end) query = query.lte('created_at', `${dateRange.end}T23:59:59`)

      const { data: headers, error } = await query
      if (error) throw error

      const rows = (headers || []) as OrderReturn[]
      const ids = rows.map((r) => r.id)
      let items: OrderReturnItem[] = []
      if (ids.length > 0) {
        const { data: itemRows } = await supabase
          .from('order_return_items')
          .select('*')
          .in('return_id', ids)
        items = (itemRows || []) as OrderReturnItem[]
      }

      const itemsByReturn = new Map<string, OrderReturnItem[]>()
      for (const item of items) {
        const list = itemsByReturn.get(item.return_id) || []
        list.push(item)
        itemsByReturn.set(item.return_id, list)
      }

      const withItems: ReturnWithItems[] = rows.map((r) => ({
        ...r,
        items: itemsByReturn.get(r.id) || [],
      }))
      setReturns(withItems)

      const outboxIds = Array.from(
        new Set(rows.flatMap((r) => r.sync_outbox_ids || []).filter(Boolean))
      )
      if (outboxIds.length > 0) {
        const { data: outboxRows } = await supabase
          .from('shopify_sync_outbox')
          .select('id, status, last_error, attempts')
          .in('id', outboxIds)
        const map: Record<string, OutboxStatus> = {}
        for (const row of outboxRows || []) {
          map[row.id] = row as OutboxStatus
        }
        setOutboxById(map)
      } else {
        setOutboxById({})
      }
    } catch (err) {
      console.error('Failed to load returns report:', err)
      setReturns([])
    } finally {
      setLoading(false)
    }
  }, [adminId, dateRange.end, dateRange.start])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return returns
    return returns.filter((r) => {
      const hay = [
        r.return_number,
        r.order_number,
        r.customer_name,
        r.scanned_barcode,
        ...(r.items || []).map((i) => i.sku),
        ...(r.items || []).map((i) => i.title),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [returns, searchQuery])

  const totals = useMemo(() => {
    return {
      returns: filtered.length,
      units: filtered.reduce((s, r) => s + (r.total_quantity || 0), 0),
      lines: filtered.reduce((s, r) => s + (r.items_count || 0), 0),
    }
  }, [filtered])

  const exportCsv = () => {
    const headers = [
      'Return Number',
      'Date',
      'Order Number',
      'Customer',
      'SKU',
      'Title',
      'Quantity',
      'Unit Price',
      'Scanned Barcode',
      'Notes',
    ]
    const lines: string[][] = [headers]
    for (const ret of filtered) {
      if (!ret.items.length) {
        lines.push([
          ret.return_number,
          new Date(ret.created_at).toISOString(),
          ret.order_number,
          ret.customer_name || '',
          '',
          '',
          String(ret.total_quantity),
          '',
          ret.scanned_barcode || '',
          ret.notes || '',
        ])
        continue
      }
      for (const item of ret.items) {
        lines.push([
          ret.return_number,
          new Date(ret.created_at).toISOString(),
          ret.order_number,
          ret.customer_name || '',
          item.sku,
          item.title,
          String(item.quantity),
          item.unit_price != null ? String(item.unit_price) : '',
          ret.scanned_barcode || '',
          ret.notes || '',
        ])
      }
    }

    const csv = lines
      .map((row) =>
        row
          .map((cell) => {
            const v = cell.replace(/"/g, '""')
            return `"${v}"`
          })
          .join(',')
      )
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `returns-report-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Returns Report
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Copy of every customer return restocked to warehouse stock.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search return #, order, customer, SKU…"
            className="w-full pl-10 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange((d) => ({ ...d, start: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-2 py-2"
          />
        </div>
        <div>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange((d) => ({ ...d, end: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-2 py-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
          <p className="text-xs text-slate-500 uppercase">Returns</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-white">{totals.returns}</p>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
          <p className="text-xs text-slate-500 uppercase">Lines</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-white">{totals.lines}</p>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
          <p className="text-xs text-slate-500 uppercase">Units</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-white">{totals.units}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner text="Loading returns…" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={RotateCcw}
          title="No returns yet"
          description="Confirmed returns from the Return Shipment page will appear here."
        />
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
          {filtered.map((ret) => {
            const expanded = expandedId === ret.id
            const statuses = (ret.sync_outbox_ids || [])
              .map((id) => outboxById[id])
              .filter(Boolean) as OutboxStatus[]
            const sync = syncLabel(statuses)

            return (
              <div key={ret.id}>
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : ret.id)}
                  className="w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {ret.return_number}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sync.className}`}>
                          {sync.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Hash className="h-3 w-3" />
                          {ret.order_number}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {ret.customer_name || '—'}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(ret.created_at).toLocaleString()}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {ret.total_quantity} unit(s) · {ret.items_count} line(s)
                        </span>
                      </div>
                    </div>
                    {expanded ? (
                      <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                    )}
                  </div>
                </button>

                {expanded && (
                  <div className="px-4 pb-4 space-y-2">
                    {ret.notes && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 rounded-lg px-3 py-2">
                        {ret.notes}
                      </p>
                    )}
                    {ret.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-800 p-2.5"
                      >
                        <ReturnThumb src={item.image_url} alt={item.title} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {item.title}
                          </p>
                          <p className="text-xs font-mono text-slate-500">{item.sku || '—'}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">
                            ×{item.quantity}
                          </p>
                          {item.unit_price != null && (
                            <p className="text-xs text-slate-500">
                              {Number(item.unit_price).toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    {statuses.some((s) => s.last_error) && (
                      <p className="text-xs text-red-600 dark:text-red-300">
                        Sync note: {statuses.find((s) => s.last_error)?.last_error}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
